import { buildTokens, tokensToCssVars } from '../customer/mealpass/storefrontTheme';
import '../customer/mealpass/storefront.css';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
// v5.24 — the SAME breakdown maths the server prices with. Display only: the
// figure a customer approves is re-derived by `price_catering_order`.
import {
  customPerPerson,
  customSlotBounds,
  customTotalItems,
  isCustomPackage,
} from '../../utils/cateringCustom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Minus,
  Plus,
  Users,
  CalendarDays,
  Clock,
  Truck,
  ShoppingBag,
  AlertTriangle,
  Check,
  Sparkles,
} from 'lucide-react';
import DishThumb from '../order/DishThumb';
import DishMacros from '../order/DishMacros';

// ─────────────────────────────────────────────────────────────────────────────
// CateringOrderModal — public storefront catering wizard (sibling of
// AddToCartModal, but standalone). Two steps:
//   1. "Plan your event"  — headcount, date/time (validated against the
//      tenant's availability window), pickup vs delivery, and the package's
//      dish picks (per-person slots) or tray quantities.
//   2. "Make it special"  — premium upsells + notes + recap.
// A sticky footer shows the LIVE price (total · per-person) on both steps.
// All pricing here is display-only — the server re-prices at checkout, and
// re-enforces lead time / hours / skip dates. No API calls; everything comes
// from props.
// ─────────────────────────────────────────────────────────────────────────────

const SERIF = { fontFamily: 'var(--sf-heading, Playfair Display, ui-serif, Georgia, serif)', fontWeight: 600 };

const pad2 = (n) => String(n).padStart(2, '0');
const toLocalDateStr = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// 12-hour label for an (hour, minute) pair — "5:30 PM".
const hourLabel = (h, m = 0) => {
  const period = h >= 12 ? 'PM' : 'AM';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${pad2(m)} ${period}`;
};

// included_items may be plain strings or {name} objects — render either.
const displayNameOf = (it) => (typeof it === 'string' ? it : it?.name || it?.label || '');

const trayKey = (itemId, size) => `${itemId}::${size}`;

const CateringOrderModal = ({
  package: pkg,
  availability,
  currencyPrefix = '$',
  brandPrimary = '#f97316',
  // v5.14.8 (founder) — a map of meal-item id → item, so slot dishes can show
  // their photo (the catering feed carries only dish names). Optional: absent
  // on surfaces that don't have the live menu — dishes just render without an
  // image there, as before.
  itemsById = {},
  marketplaceDeliveryFee = null,
  onClose,
  onSubmit,
  brand = null,
}) => {
  // ── State (hooks must run unconditionally — the !pkg guard comes AFTER) ──
  const [step, setStep] = useState(1); // 1 = event, 2 = extras
  const bodyRef = useRef(null);
  useEffect(() => { bodyRef.current?.scrollTo?.({ top: 0 }); }, [step]);
  const [persons, setPersons] = useState(() => Math.max(1, Number(pkg?.min_persons) || 1));
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('');
  const [fulfillment, setFulfillment] = useState('pickup'); // 'pickup' | 'delivery'
  const [slotPicks, setSlotPicks] = useState({}); // { [slotId]: [item_id, ...] }
  const [trayQty, setTrayQty] = useState({}); // { `${itemId}::${size}`: qty }
  const [premiumIds, setPremiumIds] = useState([]);
  // v5.17 — package ADD-ONS (priced FLAT per order) + ingredient removals.
  // KEYS ONLY leave the browser; price_catering_order re-resolves and 422s a
  // bogus pick like a bad slot selection.
  const [addonPicks, setAddonPicks] = useState([]);           // [{group, option}]
  const [removedIngredients, setRemovedIngredients] = useState([]); // [{group, option}]
  // v5.19 — INHERITED per-dish add-ons: { [meal_item_id]: [{group, option}] }.
  // The dish's OWN extras, configured once on the dish, priced per person.
  const [dishAddonPicks, setDishAddonPicks] = useState({});
  const [notes, setNotes] = useState('');

  // Reset the wizard whenever a DIFFERENT package is opened into this modal.
  useEffect(() => {
    if (!pkg) return;
    setStep(1);
    setPersons(Math.max(1, Number(pkg.min_persons) || 1));
    setEventDate('');
    setEventTime('');
    setFulfillment('pickup');
    setSlotPicks({});
    setTrayQty({});
    setPremiumIds([]);
    setAddonPicks([]);
    setRemovedIngredients([]);
    setDishAddonPicks({});
    setNotes('');
    // v5.24 round 1 — the customisation state is part of "the wizard" and was
    // the one thing this reset did not clear, so a customer who rebalanced
    // package A and then opened package B landed in customise mode they never
    // chose, carrying A's per-category allowances (slot ids fall back to the
    // slot's INDEX, so they collide across packages rather than being ignored).
    setCustomOn(false);
    setAllowance({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pkg?.id]);

  // ── Derived package shape ────────────────────────────────────────────────
  // Canonical field is `pricing_model` ("per_person" | "tray") — see
  // backend/models/catering.py CateringPackageBase.
  const pkgType = pkg?.pricing_model || pkg?.type || pkg?.package_type || 'per_person';
  const isTray = pkgType === 'tray';
  const minPersons = Math.max(1, Number(pkg?.min_persons) || 1);
  const pricePerPerson = Number(pkg?.price_per_person) || 0;
  const slots = useMemo(() => (Array.isArray(pkg?.slots) ? pkg.slots : []), [pkg]);
  // v5.19 — { [meal_item_id]: {name, addon_groups, max_total_addons} } from the
  // public feed. Keyed by DISH, not by slot: the same dish can appear in more
  // than one slot and its extras must not disagree with themselves.
  const dishAddons = useMemo(
    () => (pkg?.dish_addons && typeof pkg.dish_addons === 'object' ? pkg.dish_addons : {}),
    [pkg]
  );
  const dishAddonGroupsOf = (itemId) => {
    const g = dishAddons[itemId]?.addon_groups;
    return Array.isArray(g) ? g : [];
  };
  // v5.20 (codex P1) — classify a pick the way the SERVER's resolver does. In
  // an `ingredients` group a ZERO-priced option is a standard ingredient that
  // is already on the plate, so clicking it means "no onions" — a REMOVAL, not
  // an add-on. Sending it under `addons` made resolve_addon_selection reject it
  // as already included, so the customer got a 422 at checkout and there was no
  // path in the UI that could ever express the removal.
  const isDishRemoval = (itemId, groupKey, optionKey) => {
    const g = dishAddonGroupsOf(itemId).find((x) => String(x.key) === String(groupKey));
    if (!g || String(g.mode) !== 'ingredients') return false;
    const o = (g.options || []).find((x) => String(x.key) === String(optionKey));
    return (Number(o?.price) || 0) === 0;
  };
  const toggleDishAddon = (itemId, groupKey, optionKey) => {
    setDishAddonPicks((prev) => {
      const cur = prev[itemId] || [];
      const hit = cur.some((p) => p.group === groupKey && p.option === optionKey);
      const next = hit
        ? cur.filter((p) => !(p.group === groupKey && p.option === optionKey))
        : [...cur, { group: groupKey, option: optionKey }];
      return { ...prev, [itemId]: next };
    });
  };
  const trayItems = useMemo(
    () => (Array.isArray(pkg?.tray_items) ? pkg.tray_items : Array.isArray(pkg?.items) ? pkg.items : []),
    [pkg]
  );
  const includedItems = useMemo(
    () => (Array.isArray(pkg?.included_items) ? pkg.included_items : []),
    [pkg]
  );
  const premiumItems = useMemo(
    () => (Array.isArray(pkg?.premium_items) ? pkg.premium_items : []),
    [pkg]
  );
  // v5.17 — add-on groups ride the public package projection (coerced at the
  // write path). Same shape as meal-item groups: mode extras vs ingredients,
  // price 0 in ingredients mode = included (removable free), price > 0 =
  // premium inclusion. Catering add-ons price FLAT per order.
  const addonGroups = useMemo(
    () => (Array.isArray(pkg?.addon_groups) ? pkg.addon_groups : [])
      .filter((g) => g && g.key && Array.isArray(g.options) && g.options.length > 0),
    [pkg]
  );
  // The cap AS ENFORCED — floored at the sum of the groups' required minima,
  // mirroring utils/addon_options.effective_max_total_addons on the server.
  // Without the floor a too-low cap (two groups requiring one pick each, cap 1)
  // leaves the package unorderable from the UI even though the server accepts
  // it: the picker refuses the second required pick and addonMinUnmet then
  // blocks the CTA. Ingredients groups contribute 0 (they coerce to min 0
  // server-side); a group's min can never exceed its own option count.
  const maxTotalAddons = useMemo(() => {
    const n = parseInt(pkg?.max_total_addons, 10);
    const cap = Number.isFinite(n) && n > 0 ? n : null;
    if (cap === null) return null;
    const required = addonGroups.reduce((sum, g) => {
      if (String(g?.mode || '').toLowerCase() === 'ingredients') return sum;
      const m = parseInt(g?.min_select, 10);
      if (!Number.isFinite(m) || m <= 0) return sum;
      return sum + Math.min(m, (g.options || []).length);
    }, 0);
    return Math.max(cap, required);
  }, [pkg, addonGroups]);
  const addonOptionOf = (groupKey, optionKey) => {
    const g = addonGroups.find((x) => String(x.key) === String(groupKey));
    return g ? (g.options || []).find((o) => String(o?.key) === String(optionKey)) || null : null;
  };
  const toggleAddon = (group, option) => {
    setAddonPicks((prev) => {
      const hit = prev.findIndex((p) => p.group === group.key && p.option === option.key);
      if (hit !== -1) return prev.filter((_, i) => i !== hit);
      const inGroup = prev.filter((p) => p.group === group.key).length;
      const maxSel = parseInt(group.max_select, 10);
      if (Number.isFinite(maxSel) && maxSel > 0 && inGroup >= maxSel) return prev;
      if (maxTotalAddons && prev.length >= maxTotalAddons) return prev;
      return [...prev, { group: group.key, option: option.key }];
    });
  };
  const toggleRemovedIngredient = (group, option) => {
    setRemovedIngredients((prev) => {
      const hit = prev.findIndex((p) => p.group === group.key && p.option === option.key);
      if (hit !== -1) return prev.filter((_, i) => i !== hit);
      return [...prev, { group: group.key, option: option.key }];
    });
  };
  // Unmet extras minimums block the final CTA (ingredients coerce min 0).
  const addonMinUnmet = useMemo(() => {
    for (const g of addonGroups) {
      const min = parseInt(g.min_select, 10);
      if (!Number.isFinite(min) || min <= 0) continue;
      const picked = addonPicks.filter((p) => p.group === g.key).length;
      if (picked < min) return { group: g, min, picked };
    }
    return null;
  }, [addonGroups, addonPicks]);

  const slotIdOf = (slot, idx) => slot?.id || slot?.slot_id || `slot_${idx}`;
  const sizesOf = (item) => (Array.isArray(item?.sizes) ? item.sizes : Array.isArray(item?.tray_sizes) ? item.tray_sizes : []);
  // Tray items carry item_id (a meal-item link) but no `id` of their own —
  // key state on item_id with an index fallback for unlinked free-text dishes.
  const trayDishKey = (item, idx) => item?.item_id || `i${idx}`;

  // "Now" as a wall-clock Date in the TENANT's timezone (the backend
  // validates in tenant time — a browser in another timezone would otherwise
  // pass/fail the lead check differently than checkout will).
  const tenantNow = () => {
    const tzName = availability?.timezone;
    if (!tzName) return new Date();
    try {
      return new Date(new Date().toLocaleString('en-US', { timeZone: tzName }));
    } catch {
      return new Date();
    }
  };

  // ── Availability / datetime validation (client-side UX only) ────────────
  const leadHours = Math.max(0, Number(availability?.min_lead_hours) || 0);
  const startHour = Number.isFinite(Number(availability?.event_start_hour))
    ? Number(availability.event_start_hour)
    : 8;
  const endHour = Number.isFinite(Number(availability?.event_end_hour))
    ? Number(availability.event_end_hour)
    : 20; // latest allowed START hour, inclusive
  const skipDates = useMemo(
    () => (Array.isArray(availability?.skip_dates) ? availability.skip_dates : []),
    [availability]
  );

  // Rough date floor: tenant-today + ceil(lead/24) days (exact check below).
  const minDateStr = useMemo(() => {
    const d = tenantNow();
    d.setDate(d.getDate() + Math.ceil(leadHours / 24));
    return toLocalDateStr(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadHours, availability?.timezone]);

  // 30-minute start-time options from event_start_hour to event_end_hour inclusive.
  const timeOptions = useMemo(() => {
    const opts = [];
    for (let h = startHour; h <= endHour; h++) {
      for (const m of [0, 30]) {
        opts.push({ value: `${pad2(h)}:${pad2(m)}`, label: hourLabel(h, m) });
      }
    }
    return opts;
  }, [startHour, endHour]);

  const leadLabel =
    leadHours >= 48 && leadHours % 24 === 0 ? `${leadHours / 24} days` : `${leadHours} hours`;

  // A friendly message when the chosen datetime breaks a constraint; null when OK
  // (or when the date/time is still incomplete — that just disables Next).
  const datetimeIssue = useMemo(() => {
    if (!eventDate) return null;
    if (skipDates.includes(eventDate)) {
      return "That date isn't available — please pick another day.";
    }
    if (!eventTime) return null;
    const h = parseInt(eventTime.split(':')[0], 10);
    if (Number.isNaN(h) || h < startHour || h > endHour) {
      return `Events can start between ${hourLabel(startHour)} and ${hourLabel(endHour, 30)}.`;
    }
    const eventAt = new Date(`${eventDate}T${eventTime}:00`);
    if (Number.isNaN(eventAt.getTime())) return 'Please pick a valid date and time.';
    // Both operands are wall-clocks in the tenant's frame: eventAt is the
    // tenant-local event wall-clock, tenantNow() the tenant's current one.
    if (leadHours > 0 && eventAt.getTime() - tenantNow().getTime() < leadHours * 3600 * 1000) {
      return `We need at least ${leadLabel}' notice — please choose a later date or time.`;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventDate, eventTime, skipDates, startHour, endHour, leadHours, leadLabel, availability?.timezone]);

  const datetimeValid = Boolean(eventDate && eventTime && !datetimeIssue);
  const personsValid = persons >= minPersons;

  // ── v5.24 CUSTOM PACKAGES ────────────────────────────────────────────────
  // FOUNDER: *"the UI should be on the package once selected — Want something
  // different? Customize this package"*, and *"We need to make sure 5
  // selections are done in this case i.e. 5 items in total. A customer cannot
  // select just 1 and remove the rest and call it a day."*
  //
  // `allowance` is how many THIS customer may pick from a category. With
  // customisation closed it is simply the package's own `choose`, so the whole
  // feature is inert for every package that has not opted in.
  const customAvailable = isCustomPackage(pkg);
  const [customOn, setCustomOn] = useState(false);
  const [allowance, setAllowance] = useState({}); // { [slotId]: n }

  const allowanceFor = useCallback((slot, idx) => {
    const sid = slotIdOf(slot, idx);
    const base = Math.max(1, Number(slot?.choose) || 1);
    if (!customAvailable || !customOn) return base;
    const n = allowance[sid];
    return Number.isFinite(n) ? n : base;
  }, [customAvailable, customOn, allowance]);

  // The package's own item count — the number the picker must land on exactly.
  const targetItems = useMemo(() => customTotalItems(pkg), [pkg]);
  const allowedTotal = useMemo(
    () => slots.reduce((sum, s, i) => sum + allowanceFor(s, i), 0),
    [slots, allowanceFor],
  );

  /**
   * Move one category up or down.
   *
   * The `+` is disabled once the basket is already at the package's item count,
   * so an over-full state is unreachable rather than merely refused — the
   * customer drops something before they can add. Reducing an allowance below
   * what they have already picked TRIMS the picks, because leaving a category
   * showing more dishes than it allows is a basket that cannot be ordered.
   */
  /**
   * ONE trimming rule, reached by EVERY path that lowers what a category may
   * hold. It used to live inline in the stepper only, so "Use the standard
   * package" dropped the allowances and left the dishes they had authorised —
   * a slot holding 3 dishes under a `choose` of 2, `slotsComplete` false, and
   * no hint which dish to drop. A rule that exists on one path and is
   * re-forgotten on the next is the shape this repo keeps getting bitten by.
   */
  const trimPicksTo = useCallback((capFor) => {
    setSlotPicks((prev) => {
      let changed = false;
      const next = { ...prev };
      slots.forEach((slot, idx) => {
        const sid = slotIdOf(slot, idx);
        const cur = prev[sid] || [];
        const cap = capFor(slot, idx);
        if (cur.length > cap) { next[sid] = cur.slice(0, cap); changed = true; }
      });
      return changed ? next : prev;
    });
  }, [slots]);

  const bumpAllowance = (slot, idx, delta) => {
    const sid = slotIdOf(slot, idx);
    const { min, max } = customSlotBounds(slot);
    const next = Math.min(max, Math.max(min, allowanceFor(slot, idx) + delta));
    if (next === allowanceFor(slot, idx)) return;
    if (delta > 0 && allowedTotal >= targetItems) return;
    setAllowance((prev) => ({ ...prev, [sid]: next }));
    trimPicksTo((s, i) => (slotIdOf(s, i) === sid ? next : Infinity));
  };

  /** Back to the package exactly as the merchant sells it. */
  const resetToStandardPackage = () => {
    setCustomOn(false);
    setAllowance({});
    trimPicksTo((slot) => Math.max(1, Number(slot?.choose) || 1));
  };

  // The live per-person figure. `null` while the mix is not a legal order —
  // the picker shows the prompt rather than a price, because a number beside an
  // unorderable basket is worse than no number. THE SERVER RE-DERIVES THIS at
  // checkout; this is display only.
  const customPer = useMemo(() => {
    if (!customAvailable || !customOn) return null;
    const counts = {};
    slots.forEach((s, i) => { counts[slotIdOf(s, i)] = allowanceFor(s, i); });
    return customPerPerson(pkg, counts);
  }, [customAvailable, customOn, pkg, slots, allowanceFor]);

  // v5.24 round 1 — THE ONE RATE EVERY MONEY READER USES.
  //
  // The custom figure used to reach exactly ONE label while `total`, the
  // header, the step-2 summary, the sticky footer AND the cart payload all
  // priced the BASE package — so a rebalanced order was disclosed at one
  // number and charged at another by the server, which DOES honour the custom
  // counts (measured on the founder's $19pp package at 40 guests: 1 app/3
  // mains/1 dessert disclosed $760 and charged $800; 3/1/1 disclosed $760 and
  // charged $720). Threading `customPer` into four call sites by hand is how
  // the fifth reader gets missed, so the rate is resolved ONCE here and the
  // readers below have no base-price spelling left to reach for.
  const customActive = customAvailable && customOn;
  const effectivePerPerson = customActive ? customPer : pricePerPerson;
  // `customPer` is null while the mix is not a legal order. A NUMBER BESIDE AN
  // UNORDERABLE BASKET IS WORSE THAN NO NUMBER — and falling back to the base
  // price here would re-create the exact defect this block exists to remove.
  const perPersonKnown = effectivePerPerson != null;
  const perPersonForMath = perPersonKnown ? effectivePerPerson : 0;

  // ── Selections ───────────────────────────────────────────────────────────
  const toggleSlotPick = (slotId, itemId, choose) => {
    setSlotPicks((prev) => {
      const cur = prev[slotId] || [];
      if (cur.includes(itemId)) {
        return { ...prev, [slotId]: cur.filter((i) => i !== itemId) };
      }
      if (cur.length >= choose) return prev; // full — deselect something first
      return { ...prev, [slotId]: [...cur, itemId] };
    });
  };

  // Auto-scroll to the next incomplete slot when the current one fills up.
  // Runs as a side effect of slotPicks changing (cleaner than inside setState).
  useEffect(() => {
    if (!slots.length) return;
    // Find the first slot that's JUST been completed this render and the next
    // slot that still needs picks — then scroll there.
    let justCompletedIdx = -1;
    for (let i = 0; i < slots.length; i++) {
      const s = slots[i];
      const sid = slotIdOf(s, i);
      // v5.24 round 1 — the LIVE allowance, like every other reader in this
      // component. Reading the package's raw `choose` here made the modal park
      // on a category the customer had already satisfied at a lowered
      // allowance, and re-scroll to it on every subsequent pick.
      const req = allowanceFor(s, i);
      const picked = slotPicks[sid] || [];
      if (picked.length === req) {
        justCompletedIdx = i;
      } else if (justCompletedIdx >= 0 && picked.length < req) {
        // This is the next incomplete slot after a completed one
        const el = document.querySelector(`[data-testid="catering-slot-${sid}"]`);
        if (el) {
          setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 200);
        }
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotPicks, allowanceFor]);

  const bumpTray = (itemId, size, delta) => {
    setTrayQty((prev) => {
      const k = trayKey(itemId, size);
      return { ...prev, [k]: Math.max(0, (prev[k] || 0) + delta) };
    });
  };

  const togglePremium = (id) => {
    setPremiumIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  };

  const slotsComplete = useMemo(
    () =>
      // Every category filled to its allowance AND — when customising — the
      // basket sitting exactly on the package's item count. The second half is
      // the founder's rule, and it is checked here rather than trusted from the
      // steppers so no path can reach checkout one item short.
      slots.every((slot, idx) => (
        (slotPicks[slotIdOf(slot, idx)] || []).length === allowanceFor(slot, idx)
      )) && (!(customAvailable && customOn) || allowedTotal === targetItems),
    [slots, slotPicks, allowanceFor, customAvailable, customOn, allowedTotal, targetItems]
  );

  // Flattened tray lines with qty > 0 (payload shape + display extras).
  const trayLines = useMemo(() => {
    const out = [];
    trayItems.forEach((item, idx) => {
      sizesOf(item).forEach((sz) => {
        const qty = trayQty[trayKey(trayDishKey(item, idx), sz.size)] || 0;
        if (qty > 0) {
          out.push({
            item_id: item.item_id ?? null,
            name: item.name,
            size: sz.size,
            quantity: qty,
            _label: sz.label || sz.size,
            _price: Number(sz.price) || 0,
            _serves: Number(sz.serves) || 0,
          });
        }
      });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trayItems, trayQty]);

  const traySubtotal = trayLines.reduce((s, t) => s + t._price * t.quantity, 0);
  const servesTotal = trayLines.reduce((s, t) => s + t._serves * t.quantity, 0);

  // A basket whose price we cannot state is a basket we must not let anyone buy
  // — the server would price it differently, which is the whole defect above.
  const picksValid = (isTray ? trayLines.length > 0 : slotsComplete) && perPersonKnown;
  const step1Valid = personsValid && datetimeValid && picksValid;

  // ── Live pricing (display only — server re-prices at checkout) ──────────
  const selectedPremiums = premiumItems.filter((p) => premiumIds.includes(p.id));
  const perPersonPremiumSum = selectedPremiums
    .filter((p) => (p.pricing_mode || 'flat') === 'per_person')
    .reduce((s, p) => s + (Number(p.price) || 0), 0);
  const flatPremiumSum = selectedPremiums
    .filter((p) => (p.pricing_mode || 'flat') !== 'per_person')
    .reduce((s, p) => s + (Number(p.price) || 0), 0);

  // v5.17 — picked add-ons sum FLAT per order (mirrors price_catering_order;
  // the server stays authoritative). Removals never charge.
  const addonsSum = addonPicks.reduce((s, p) => {
    const price = Number(addonOptionOf(p.group, p.option)?.price) || 0;
    return s + (price > 0 ? price : 0);
  }, 0);

  // v5.19 — INHERITED per-dish add-ons, PER PERSON PER DISH. A $2 extra on a
  // dish every guest is served costs $2 x headcount, which is what a caterer
  // actually charges — mirrors _price_dish_addons on the server, which stays
  // authoritative.
  // v5.20 (codex P2 ×2) — derived from the ACTIVE slot selections, not from
  // every entry ever stored in dishAddonPicks. Two ways the old item-keyed sum
  // disagreed with the server:
  //   • a dish deselected after its extra was chosen kept charging on screen
  //     (toggleSlotPick doesn't clear the picks), while the payload dropped it;
  //   • a dish offered in TWO slots and picked in both was counted once here
  //     and twice by the server, which prices slot by slot.
  // Walking the same slots the payload walks makes the two agree by
  // construction rather than by remembering to keep them in step.
  const dishAddonPerPersonSum = useMemo(() => {
    let sum = 0;
    (slots || []).forEach((slot, idx) => {
      const sid = slotIdOf(slot, idx);
      (slotPicks[sid] || []).forEach((itemId) => {
        const groups = dishAddonGroupsOf(itemId);
        (dishAddonPicks[itemId] || []).forEach((p) => {
          const g = groups.find((x) => String(x.key) === String(p.group));
          const o = (g?.options || []).find((x) => String(x.key) === String(p.option));
          const price = Number(o?.price) || 0;
          if (price > 0) sum += price;
        });
      });
    });
    return sum;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dishAddonPicks, dishAddons, slotPicks, slots]);

  const total = (isTray
    ? traySubtotal + persons * perPersonPremiumSum + flatPremiumSum
    : persons * (perPersonForMath + perPersonPremiumSum + dishAddonPerPersonSum)
      + flatPremiumSum) + addonsSum;
  const perPerson = persons > 0 ? total / persons : 0;

  const money = (n) => `${currencyPrefix}${round2(n).toFixed(2)}`;

  // ── Brand accents (inline styles — brandPrimary is a hex string prop) ────
  const isHex = /^#([0-9a-fA-F]{6})$/.test(brandPrimary || '');
  const brandTint = isHex ? `${brandPrimary}14` : 'rgba(0,0,0,0.04)'; // ~8% fill
  const selectedChipStyle = { borderColor: brandPrimary, background: brandTint };

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleAddToCart = () => {
    onSubmit({
      persons,
      event_date: eventDate,
      event_time: eventTime,
      fulfillment_method: fulfillment,
      selections: isTray
        ? []
        : slots.map((slot, idx) => {
            const picked = slotPicks[slotIdOf(slot, idx)] || [];
            return {
              slot_id: slotIdOf(slot, idx),
              item_ids: picked,
              // v5.19 — the dish's inherited extras ride WITH the dish that
              // owns them. KEYS ONLY; price_catering_order re-resolves against
              // the meal item and 422s a pick the dish does not offer.
              // v5.20 — split the picks into the two things the resolver
              // understands: paid/optional extras vs standard ingredients the
              // guest asked us to leave out.
              item_addons: picked
                .filter((id) => (dishAddonPicks[id] || []).length > 0)
                .map((id) => {
                  const picks = dishAddonPicks[id] || [];
                  return {
                    item_id: id,
                    addons: picks.filter((p) => !isDishRemoval(id, p.group, p.option)),
                    removed_ingredients: picks.filter(
                      (p) => isDishRemoval(id, p.group, p.option)),
                  };
                }),
            };
          }),
      tray_selections: trayLines.map(({ item_id, name, size, quantity }) => ({
        item_id,
        name,
        size,
        quantity,
      })),
      premium_ids: premiumIds,
      // v5.17 — package add-on picks + ingredient removals (KEYS only; the
      // server re-resolves every price and bound at checkout).
      catering_addons: addonPicks,
      catering_removed_ingredients: removedIngredients,
      notes,
      price_total: round2(total),
      per_person: round2(perPerson),
    });
    // Parent closes the modal + navigates — no navigation here.
  };

  const timeLabelOf = (value) => timeOptions.find((t) => t.value === value)?.label || value;

  // v5.17 — package ADD-ONS render on STEP 1, revealed as soon as the guest
  // picks a dish (founder: "keep the add-ons on step 1 … so they know they can
  // modify this list right on step 1"). Same vocabulary as the /order picker:
  // extras pick-to-add, ingredients Included ↔ Removed, premium inclusions
  // charge. Prices are FLAT per order; the server re-resolves every one.
  const anyDishPicked = isTray
    ? trayLines.length > 0
    : Object.values(slotPicks).some((ids) => (ids || []).length > 0);
  const addonSections = (
    <>
      {addonGroups.map((g) => {
        const isIngredients = g.mode === 'ingredients';
        const maxSel = parseInt(g.max_select, 10);
        const minSel = parseInt(g.min_select, 10);
        const hint = [
          Number.isFinite(minSel) && minSel > 0 ? `pick at least ${minSel}` : null,
          Number.isFinite(maxSel) && maxSel > 0 ? `up to ${maxSel}` : null,
        ].filter(Boolean).join(' · ');
        const capReached = !!(maxTotalAddons && addonPicks.length >= maxTotalAddons);
        return (
          <section key={g.key} className="rounded-2xl border border-neutral-200 bg-white p-4" data-testid="catering-addon-group">
            <Label className="font-semibold text-neutral-900 mb-1 block">
              {g.label || g.key}
              {hint ? <span className="font-normal text-neutral-400 text-xs"> · {hint}</span> : null}
            </Label>
            <p className="text-xs text-neutral-500 mb-3">
              {isIngredients
                ? 'Remove anything you don’t want — or add a premium upgrade.'
                : 'Optional extras, charged once for the whole order.'}
            </p>
            <div className="space-y-2">
              {(g.options || []).map((o) => {
                const priced = (Number(o.price) || 0) > 0;
                const included = isIngredients && !priced;
                if (included) {
                  const removed = removedIngredients.some((p) => p.group === g.key && p.option === o.key);
                  return (
                    <button key={o.key} type="button" onClick={() => toggleRemovedIngredient(g, o)}
                      className={`w-full flex items-center justify-between rounded-xl border-2 px-4 py-2.5 text-sm transition-colors ${removed ? 'border-red-200 bg-red-50 text-red-700' : 'border-neutral-200 text-neutral-800 hover:border-neutral-300'}`}
                      data-testid="catering-ingredient-option">
                      <span className={removed ? 'line-through' : ''}>{o.label || o.key}</span>
                      <span className="text-xs font-semibold">{removed ? 'Removed' : 'Included'}</span>
                    </button>
                  );
                }
                const picked = addonPicks.some((p) => p.group === g.key && p.option === o.key);
                const inGroup = addonPicks.filter((p) => p.group === g.key).length;
                const groupFull = Number.isFinite(maxSel) && maxSel > 0 && inGroup >= maxSel;
                const blocked = !picked && (capReached || groupFull);
                return (
                  <button key={o.key} type="button" onClick={() => toggleAddon(g, o)}
                    disabled={blocked}
                    className={`w-full flex items-center justify-between rounded-xl border-2 px-4 py-2.5 text-sm transition-colors disabled:opacity-40 ${picked ? '' : 'border-neutral-200 text-neutral-800 hover:border-neutral-300'}`}
                    style={picked ? selectedChipStyle : undefined}
                    data-testid="catering-addon-option">
                    <span className="font-medium flex items-center gap-1.5">
                      {picked && <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: brandPrimary }} />}
                      {o.label || o.key}
                    </span>
                    <span className="text-xs font-semibold" style={picked ? { color: brandPrimary } : { color: '#737373' }}>
                      {priced ? `+${money(Number(o.price) || 0)}` : 'Free'}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
      {maxTotalAddons ? (
        <p className="text-[11px] text-neutral-400 -mt-3" data-testid="catering-addon-cap-note">
          Up to {maxTotalAddons} add-on{maxTotalAddons === 1 ? '' : 's'} per order
          {addonPicks.length >= maxTotalAddons ? ' — limit reached' : ''}.
        </p>
      ) : null}
      {addonMinUnmet && (
        <p className="text-xs text-amber-700 -mt-2" data-testid="catering-addon-min-note">
          Pick at least {addonMinUnmet.min} from {addonMinUnmet.group.label || addonMinUnmet.group.key} to continue.
        </p>
      )}
    </>
  );

  if (!pkg) return null;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className="sm:max-w-2xl p-0 sm:p-0 gap-0 bg-white sm:rounded-2xl sf-root !z-[91]"
        overlayClassName="!z-[90]"
        data-testid="catering-order-modal"
        aria-describedby="catering-description"
        style={{ ...(brand ? tokensToCssVars(buildTokens(brand)) : {}), background: '#ffffff', overflow: 'hidden' }}
      >
        {/* ── Header ── */}
        <DialogHeader className="shrink-0 px-5 sm:px-6 pt-5 sm:pt-6 pb-4 border-b border-neutral-100 text-left sm:text-left space-y-1">
          <p className="text-[11px] tracking-[0.3em] uppercase text-neutral-500">Catering</p>
          <DialogTitle className="text-xl sm:text-2xl leading-tight text-neutral-900 pr-6" style={SERIF}>
            {pkg.name}
          </DialogTitle>
          <p id="catering-description" className="text-sm text-neutral-600 leading-relaxed line-clamp-2">{pkg.description || 'Choose your event details and menu, then review your order.'}</p>
          <div className="flex items-center gap-2 text-xs font-medium pt-2" data-testid="catering-step-indicator">
            <span className={step === 1 ? '' : 'text-neutral-400'} style={step === 1 ? { color: brandPrimary } : undefined}>
              1. Plan your event
            </span>
            <span className="text-neutral-300">→</span>
            <span className={step === 2 ? '' : 'text-neutral-400'} style={step === 2 ? { color: brandPrimary } : undefined}>
              2. Make it special
            </span>
          </div>
        </DialogHeader>

        {/* One scroll area keeps all dishes reachable and the footer visible. */}
        <div ref={bodyRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 sm:px-6 py-5 space-y-5 bg-neutral-50/60" data-testid="catering-scroll-body">
          {/* ═════════ STEP 1 — PLAN YOUR EVENT ═════════ */}
          {step === 1 && (
            <div className="space-y-5" data-testid="catering-step-event">
              {/* Headcount */}
              <section className="rounded-2xl border border-neutral-200 bg-white p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-neutral-400" />
                  <Label htmlFor="catering-persons" className="font-semibold text-neutral-900">How many guests?</Label>
                </div>
                <p className="text-xs text-neutral-500 mb-3">
                  {!isTray && perPersonKnown && `${money(effectivePerPerson)}/person · `}
                  Minimum {minPersons} guests
                </p>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setPersons((p) => Math.max(minPersons, p - 1))}
                    disabled={persons <= minPersons}
                    className="w-10 h-10 rounded-full border-2 border-neutral-200 flex items-center justify-center text-neutral-700 hover:border-neutral-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="catering-persons-dec"
                    aria-label="Fewer guests"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <Input
                    id="catering-persons"
                    type="number"
                    inputMode="numeric"
                    min={minPersons}
                    value={persons}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setPersons(Number.isNaN(v) ? 0 : v);
                    }}
                    onBlur={() => setPersons((p) => Math.max(minPersons, p))}
                    className="w-24 text-center text-lg font-semibold"
                    data-testid="catering-persons-input"
                  />
                  <button
                    type="button"
                    onClick={() => setPersons((p) => Math.max(minPersons, p) + 1)}
                    className="w-10 h-10 rounded-full border-2 border-neutral-200 flex items-center justify-center text-neutral-700 hover:border-neutral-400 transition-colors"
                    data-testid="catering-persons-inc"
                    aria-label="More guests"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-neutral-500">guests</span>
                </div>
                {!personsValid && (
                  <p className="mt-2 text-xs font-medium text-amber-700" data-testid="catering-min-persons-warning">
                    Minimum {minPersons} guests
                  </p>
                )}
              </section>

              {/* Date + time */}
              <section className="rounded-2xl border border-neutral-200 bg-white p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CalendarDays className="w-4 h-4 text-neutral-400" />
                  <Label className="font-semibold text-neutral-900">When is your event?</Label>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="catering-date" className="text-xs text-neutral-500 mb-1.5 block">Date</Label>
                    <Input
                      id="catering-date"
                      type="date"
                      value={eventDate}
                      min={minDateStr}
                      onChange={(e) => setEventDate(e.target.value)}
                      data-testid="catering-date-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="catering-time" className="text-xs text-neutral-500 mb-1.5 block">Start time</Label>
                    <div className="relative">
                      <Clock className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <select
                        id="catering-time"
                        value={eventTime}
                        onChange={(e) => setEventTime(e.target.value)}
                        className="w-full h-10 rounded-md border border-input bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        data-testid="catering-time-select"
                      >
                        <option value="">Select a time…</option>
                        {timeOptions.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                {datetimeIssue && (
                  <div
                    className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5"
                    data-testid="catering-datetime-warning"
                  >
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-800">{datetimeIssue}</p>
                  </div>
                )}
              </section>

              {/* Pickup vs delivery */}
              <section className="rounded-2xl border border-neutral-200 bg-white p-4">
                <Label className="font-semibold text-neutral-900 mb-3 block">
                  How should we get it to you?
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'pickup', label: 'Pickup', Icon: ShoppingBag },
                    { value: 'delivery', label: 'Delivery', Icon: Truck },
                  ].map(({ value, label, Icon }) => {
                    const active = fulfillment === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setFulfillment(value)}
                        className={`flex items-center justify-center gap-2 rounded-xl border-2 py-3 text-sm font-medium transition-colors ${
                          active ? 'text-neutral-900' : 'border-neutral-200 text-neutral-500 hover:border-neutral-300'
                        }`}
                        style={active ? selectedChipStyle : undefined}
                        data-testid={`catering-fulfillment-${value}`}
                      >
                        <Icon className="w-4 h-4" style={active ? { color: brandPrimary } : undefined} />
                        {label}
                      </button>
                    );
                  })}
                </div>
                {fulfillment === 'delivery' && marketplaceDeliveryFee != null && (
                  <div
                    className="mt-3 rounded-xl border border-orange-200 bg-orange-50 px-3 py-2.5"
                    data-testid="catering-marketplace-delivery-fee"
                  >
                    <p className="text-xs font-semibold text-orange-950">
                      YOFO marketplace delivery is a flat CA${Number(marketplaceDeliveryFee).toFixed(2)}.
                    </p>
                  </div>
                )}
              </section>

              {/* v5.24 — FOUNDER COPY, VERBATIM: *"the UI should be on the
                  package once selected — Want something different? Customize
                  this package"*. Only appears on a package the merchant opted
                  in; every other package is untouched. */}
              {!isTray && customAvailable && (
                <section
                  className="rounded-2xl border border-neutral-200 bg-white p-4"
                  data-testid="catering-customize-cta"
                >
                  {!customOn ? (
                    <button
                      type="button"
                      onClick={() => setCustomOn(true)}
                      className="text-left w-full"
                      data-testid="catering-customize-open"
                    >
                      <span className="block font-semibold text-neutral-900">
                        Want something different?
                      </span>
                      <span className="block text-sm mt-0.5" style={{ color: brandPrimary }}>
                        Customize this package
                      </span>
                      <span className="block text-xs text-neutral-500 mt-1">
                        Same {targetItems} items — you choose the mix.
                      </span>
                    </button>
                  ) : (
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <span className="block font-semibold text-neutral-900">
                          {/* THE COUNTER IS THE RULE MADE VISIBLE. Add-to-cart
                              stays shut until this reads N of N. */}
                          <span data-testid="catering-custom-counter">
                            {allowedTotal} of {targetItems} items
                          </span>
                        </span>
                        <span className="block text-xs text-neutral-500 mt-0.5">
                          {allowedTotal === targetItems
                            ? 'Use − and + on each category to change the mix.'
                            : 'Adjust a category to get back to '
                              + `${targetItems} items.`}
                        </span>
                      </div>
                      <div className="text-right shrink-0">
                        {customPer != null && (
                          <span
                            className="block font-semibold text-neutral-900"
                            data-testid="catering-custom-price"
                          >
                            {money(customPer)}
                            <span className="text-xs font-normal text-neutral-500"> /person</span>
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={resetToStandardPackage}
                          className="text-xs text-neutral-500 underline mt-1"
                          data-testid="catering-customize-reset"
                        >
                          Use the standard package
                        </button>
                      </div>
                    </div>
                  )}
                </section>
              )}

              {/* Per-person mode: dish picks per slot */}
              {!isTray &&
                slots.map((slot, idx) => {
                  const sid = slotIdOf(slot, idx);
                  // v5.24 — the customer's OWN allowance when they are
                  // rebalancing; the package's `choose` otherwise, so an
                  // untouched package renders exactly as it always has.
                  const choose = allowanceFor(slot, idx);
                  const bounds = customSlotBounds(slot);
                  const canCustomise = customAvailable && customOn;
                  const picked = slotPicks[sid] || [];
                  const complete = picked.length === choose;
                  const entries = Object.entries(slot?.item_names || {});
                  return (
                    <section
                      key={sid}
                      className={`rounded-2xl border bg-white p-4 transition-colors duration-500 ${
                        complete ? 'border-green-200 bg-green-50/30' : 'border-neutral-200'
                      }`}
                      data-testid={`catering-slot-${sid}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <Label className="font-semibold text-neutral-900 flex items-center gap-2">
                          Choose {choose} · {slot?.name}
                          {complete && (
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-500 text-white animate-[scale-in_0.3s_ease-out]" data-testid={`slot-complete-${sid}`}>
                              <Check className="w-3 h-3" />
                            </span>
                          )}
                        </Label>
                        <div className="flex items-center gap-2">
                          {canCustomise && (
                            <div
                              className="flex items-center gap-1 rounded-full border border-neutral-200 px-1"
                              data-testid={`catering-custom-stepper-${sid}`}
                            >
                              <button
                                type="button"
                                aria-label={`One fewer ${slot?.name || 'item'}`}
                                disabled={choose <= bounds.min}
                                onClick={() => bumpAllowance(slot, idx, -1)}
                                className="w-6 h-6 rounded-full text-neutral-700 disabled:opacity-30"
                              >
                                −
                              </button>
                              <span className="text-xs font-semibold tabular-nums w-4 text-center">
                                {choose}
                              </span>
                              <button
                                type="button"
                                aria-label={`One more ${slot?.name || 'item'}`}
                                disabled={choose >= bounds.max || allowedTotal >= targetItems}
                                onClick={() => bumpAllowance(slot, idx, 1)}
                                className="w-6 h-6 rounded-full text-neutral-700 disabled:opacity-30"
                              >
                                +
                              </button>
                            </div>
                          )}
                          <span
                            className={`text-xs font-semibold transition-colors ${complete ? 'text-green-600' : 'text-amber-600'}`}
                          >
                            {picked.length}/{choose} selected
                          </span>
                        </div>
                      </div>
                      {/* v5.14.8 (founder) — dishes are selectable IMAGE cards
                          (the photo comes from the live menu via itemsById; a
                          plate glyph stands in when a dish has none). */}
                      <div className="grid grid-cols-3 gap-2">
                        {entries.map(([itemId, itemName]) => {
                          const active = picked.includes(itemId);
                          const full = !active && picked.length >= choose;
                          const dishImg = itemsById[itemId]?.image_url;
                          return (
                            <button
                              key={itemId}
                              type="button"
                              disabled={full}
                              onClick={() => toggleSlotPick(sid, itemId, choose)}
                              className={`relative rounded-xl overflow-hidden border-2 text-left transition-all ${
                                active
                                  ? 'shadow-sm'
                                  : full
                                  ? 'border-neutral-100 opacity-40 cursor-not-allowed'
                                  : 'border-neutral-200 hover:border-neutral-300'
                              }`}
                              style={active ? { borderColor: brandPrimary } : undefined}
                              data-testid={`catering-pick-${sid}-${itemId}`}
                            >
                              <div className="h-16 w-full bg-neutral-100">
                                {dishImg ? (
                                  <img src={dishImg} alt="" loading="lazy"
                                    className="h-full w-full object-cover" />
                                ) : (
                                  <div className="h-full w-full flex items-center justify-center text-lg opacity-40">🍽️</div>
                                )}
                              </div>
                              <div className="px-2 py-1.5">
                                <span className="block text-xs font-medium text-neutral-700 line-clamp-2">{itemName}</span>
                                <DishMacros nutrition={itemsById[itemId]?.nutritional_info} className="mt-0.5" />
                              </div>
                              {active && (
                                <span className="absolute top-1 right-1 h-5 w-5 rounded-full flex items-center justify-center text-white"
                                  style={{ background: brandPrimary }}>
                                  <Check className="w-3 h-3" />
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {/* v5.19 — INHERITED per-dish extras. They appear only for
                          the dishes actually PICKED, under the slot that owns
                          them, so the guest customises the dish in front of them
                          rather than hunting a package-wide add-on list. The
                          merchant configured these once on the dish itself. */}
                      {picked
                        .filter((itemId) => dishAddonGroupsOf(itemId).length > 0)
                        .map((itemId) => (
                          <div key={`da-${itemId}`} className="mt-3 rounded-xl bg-neutral-50 p-3"
                            data-testid={`catering-dish-addons-${itemId}`}>
                            <p className="text-[11px] uppercase tracking-wide text-neutral-400 font-semibold mb-2">
                              Extras for {slot?.item_names?.[itemId] || dishAddons[itemId]?.name}
                            </p>
                            {dishAddonGroupsOf(itemId).map((g) => (
                              <div key={g.key} className="mb-2 last:mb-0">
                                <p className="text-xs text-neutral-500 mb-1">{g.label || g.key}</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {(g.options || []).map((o) => {
                                    const on = (dishAddonPicks[itemId] || []).some(
                                      (p) => p.group === g.key && p.option === o.key);
                                    const price = Number(o.price) || 0;
                                    return (
                                      <button
                                        key={o.key}
                                        type="button"
                                        onClick={() => toggleDishAddon(itemId, g.key, o.key)}
                                        className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                                          on ? 'shadow-sm' : 'border-neutral-200 hover:border-neutral-300'
                                        }`}
                                        style={on ? selectedChipStyle : undefined}
                                        data-testid={`catering-dish-addon-${itemId}-${o.key}`}
                                      >
                                        {/* v5.20 — a standard ingredient is
                                            already on the plate, so picking it
                                            means LEAVING IT OUT. Say that,
                                            rather than showing it like an
                                            extra the guest is adding. */}
                                        {isDishRemoval(itemId, g.key, o.key) && on ? 'No ' : ''}
                                        {o.label || o.key}
                                        {price > 0 && (
                                          <span className="ml-1 text-neutral-500">
                                            +{money(price)}/person
                                          </span>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}
                    </section>
                  );
                })}

              {/* Per-person mode: always-included row */}
              {!isTray && includedItems.length > 0 && (
                <div className="rounded-2xl border-2 border-dashed border-neutral-300 bg-neutral-50 p-4" data-testid="catering-included-items">
                  <p className="text-sm uppercase tracking-wide text-neutral-700 font-bold mb-2 flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    Always Included
                  </p>
                  <p className="text-base font-medium text-neutral-800 leading-relaxed">
                    {includedItems.map(displayNameOf).filter(Boolean).join(' · ')}
                  </p>
                </div>
              )}

              {/* Tray mode: quantity rows per dish/size */}
              {isTray && (
                <section className="rounded-2xl border border-neutral-200 bg-white p-4">
                  <Label className="font-semibold text-neutral-900 mb-1 block">Build your spread</Label>
                  <p className="text-xs text-neutral-500 mb-4">
                    Pick tray sizes and quantities — we'll tally how many guests it serves.
                  </p>
                  <div className="space-y-4">
                    {trayItems.map((item, idx) => {
                      const dishKey = trayDishKey(item, idx);
                      return (
                      <div key={dishKey} className="border-t border-neutral-100 pt-3 first:border-t-0 first:pt-0">
                        <p className="text-sm font-semibold text-neutral-800 mb-2">{item.name}</p>
                        <div className="space-y-2">
                          {sizesOf(item).map((sz) => {
                            const qty = trayQty[trayKey(dishKey, sz.size)] || 0;
                            return (
                              <div
                                key={sz.size}
                                className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2 transition-colors ${
                                  qty > 0 ? '' : 'border-neutral-100'
                                }`}
                                style={qty > 0 ? selectedChipStyle : undefined}
                                data-testid={`catering-tray-row-${dishKey}-${sz.size}`}
                              >
                                <div className="min-w-0">
                                  <p className="text-sm text-neutral-800 truncate">{sz.label || sz.size}</p>
                                  <p className="text-xs text-neutral-500">
                                    serves {Number(sz.serves) || 0} · {money(Number(sz.price) || 0)}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => bumpTray(dishKey, sz.size, -1)}
                                    disabled={qty === 0}
                                    className="w-8 h-8 rounded-full border-2 border-neutral-200 flex items-center justify-center text-neutral-700 hover:border-neutral-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                    data-testid={`catering-tray-dec-${dishKey}-${sz.size}`}
                                    aria-label={`Fewer ${item.name} ${sz.label || sz.size}`}
                                  >
                                    <Minus className="w-3.5 h-3.5" />
                                  </button>
                                  <span className="w-6 text-center text-sm font-semibold" data-testid={`catering-tray-qty-${dishKey}-${sz.size}`}>
                                    {qty}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => bumpTray(dishKey, sz.size, 1)}
                                    className="w-8 h-8 rounded-full border-2 border-neutral-200 flex items-center justify-center text-neutral-700 hover:border-neutral-400 transition-colors"
                                    data-testid={`catering-tray-inc-${dishKey}-${sz.size}`}
                                    aria-label={`More ${item.name} ${sz.label || sz.size}`}
                                  >
                                    <Plus className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                  {/* Running serves vs headcount */}
                  <div
                    className={`mt-4 rounded-xl px-3 py-2.5 text-xs font-medium ${
                      servesTotal >= persons ? 'bg-neutral-50 text-neutral-600' : 'bg-amber-50 text-amber-800 border border-amber-200'
                    }`}
                    data-testid="catering-serves-total"
                  >
                    Serves ~{servesTotal} total
                    {servesTotal >= persons
                      ? ` — covers your ${persons} guests`
                      : ` — you have ${persons} guests, consider adding more`}
                  </div>
                </section>
              )}

              {/* Add-ons appear here the moment a dish is picked (founder). */}
              {addonGroups.length > 0 && anyDishPicked && addonSections}
            </div>
          )}

          {/* ═════════ STEP 2 — MAKE IT SPECIAL ═════════ */}
          {step === 2 && (
            <div className="space-y-5" data-testid="catering-step-extras">
              {/* Premium upsells */}
              {premiumItems.length > 0 && (
                <section className="rounded-2xl border border-neutral-200 bg-white p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-neutral-400" />
                    <Label className="font-semibold text-neutral-900">Premium touches</Label>
                  </div>
                  <div className="space-y-2">
                    {premiumItems.map((prem) => {
                      const active = premiumIds.includes(prem.id);
                      const perPersonMode = (prem.pricing_mode || 'flat') === 'per_person';
                      const priceLabel = perPersonMode
                        ? `+${money(Number(prem.price) || 0)}/person`
                        : `+${money(Number(prem.price) || 0)} flat`;
                      return (
                        <button
                          key={prem.id}
                          type="button"
                          onClick={() => togglePremium(prem.id)}
                          className={`w-full text-left rounded-xl border-2 px-4 py-3 transition-colors ${
                            active ? '' : 'border-neutral-200 hover:border-neutral-300'
                          }`}
                          style={active ? selectedChipStyle : undefined}
                          data-testid={`catering-premium-${prem.id}`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-neutral-900 flex items-center gap-1.5">
                                {active && <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: brandPrimary }} />}
                                {prem.name}
                              </p>
                              {prem.description && (
                                <p className="text-xs text-neutral-500 mt-0.5">{prem.description}</p>
                              )}
                            </div>
                            <span
                              className="text-xs font-semibold flex-shrink-0"
                              style={active ? { color: brandPrimary } : { color: '#737373' }}
                            >
                              {priceLabel}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* Package add-ons live on STEP 1 now (founder, 2026-07-30) —
                  they appear the moment a dish is picked, so a guest sees the
                  list is modifiable right there instead of a step later. */}

              {/* Notes */}
              <section className="rounded-2xl border border-neutral-200 bg-white p-4">
                <Label htmlFor="catering-notes" className="font-semibold text-neutral-900 mb-2 block">
                  Anything we should know?
                </Label>
                <Textarea
                  id="catering-notes"
                  placeholder="Allergies, setup instructions, delivery details…"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  data-testid="catering-notes"
                />
              </section>

              {/* Order summary recap */}
              <section className="rounded-2xl border border-neutral-200 bg-white p-4" data-testid="catering-summary">
                <p className="text-[11px] uppercase tracking-wide text-neutral-400 font-semibold mb-2">
                  Your event
                </p>
                <p className="text-lg text-neutral-900 mb-2" style={SERIF}>
                  {pkg.name}
                </p>
                <div className="text-sm text-neutral-600 space-y-1">
                  <p>
                    {eventDate} at {timeLabelOf(eventTime)} · {persons} guests ·{' '}
                    {fulfillment === 'delivery' ? 'Delivery' : 'Pickup'}
                  </p>
                  {!isTray &&
                    slots.map((slot, idx) => {
                      const sid = slotIdOf(slot, idx);
                      const names = (slotPicks[sid] || [])
                        .map((itemId) => (slot?.item_names || {})[itemId])
                        .filter(Boolean);
                      if (names.length === 0) return null;
                      return (
                        <p key={sid}>
                          <span className="text-neutral-400">{slot?.name}:</span> {names.join(', ')}
                        </p>
                      );
                    })}
                  {isTray &&
                    trayLines.map((line) => (
                      <p key={trayKey(line.item_id, line.size)}>
                        {line.quantity} × {line._label} — {line.name}
                      </p>
                    ))}
                  {selectedPremiums.length > 0 && (
                    <p>
                      <span className="text-neutral-400">Extras:</span>{' '}
                      {selectedPremiums.map((p) => p.name).join(', ')}
                    </p>
                  )}
                  {(addonPicks.length > 0 || removedIngredients.length > 0) && (
                    <p data-testid="catering-summary-addons">
                      <span className="text-neutral-400">Add-ons:</span>{' '}
                      {[
                        ...addonPicks.map((p) => `+ ${addonOptionOf(p.group, p.option)?.label || p.option}`),
                        ...removedIngredients.map((p) => `No ${addonOptionOf(p.group, p.option)?.label || p.option}`),
                      ].join(' · ')}
                    </p>
                  )}
                </div>

                {/* v5.17 (founder: "show summaries") — WHAT MAKES UP the price,
                    itemised, instead of one bare number. Taxes and any courier
                    fee belong to the store/checkout, not to this modal, so they
                    are named as still-to-come rather than guessed at. */}
                <div className="mt-3 pt-3 border-t border-neutral-200 text-sm space-y-1"
                  data-testid="catering-summary-price">
                  <div className="flex justify-between text-neutral-600">
                    <span>
                      {isTray
                        ? `Trays (${trayLines.reduce((s, t) => s + t.quantity, 0)})`
                        : `${money(perPersonForMath)} × ${persons} guests`}
                    </span>
                    <span className="tabular-nums">
                      {money(isTray ? traySubtotal : persons * perPersonForMath)}
                    </span>
                  </div>
                  {perPersonPremiumSum > 0 && (
                    <div className="flex justify-between text-neutral-600">
                      <span>Extras (per guest)</span>
                      <span className="tabular-nums">{money(persons * perPersonPremiumSum)}</span>
                    </div>
                  )}
                  {flatPremiumSum > 0 && (
                    <div className="flex justify-between text-neutral-600">
                      <span>Extras (per order)</span>
                      <span className="tabular-nums">{money(flatPremiumSum)}</span>
                    </div>
                  )}
                  {addonsSum > 0 && (
                    <div className="flex justify-between text-neutral-600"
                      data-testid="catering-summary-price-addons">
                      <span>Add-ons</span>
                      <span className="tabular-nums">{money(addonsSum)}</span>
                    </div>
                  )}
                  {/* v5.19 — inherited dish extras, PER PERSON. Shown with the
                      per-person rate as well as the line total so the number is
                      checkable rather than something the guest has to trust. */}
                  {dishAddonPerPersonSum > 0 && (
                    <div className="flex justify-between text-neutral-600"
                      data-testid="catering-summary-price-dish-addons">
                      <span>Dish extras ({money(dishAddonPerPersonSum)}/person)</span>
                      <span className="tabular-nums">
                        {money(dishAddonPerPersonSum * persons)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-neutral-900 pt-1">
                    <span>Subtotal</span>
                    <span className="tabular-nums">{money(total)}</span>
                  </div>
                  <p className="text-[11px] text-neutral-400 pt-1">
                    Taxes{fulfillment === 'delivery' ? ' and delivery' : ''} are shown before you pay.
                  </p>
                </div>
              </section>
            </div>
          )}
        </div>

        {/* ── Sticky footer: live price + step controls ── */}
        <div
          className="shrink-0 z-10 border-t border-neutral-200 bg-white/95 backdrop-blur px-5 sm:px-6 pt-4"
          data-testid="catering-footer"
          style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
        >
          <div className="flex flex-wrap gap-x-3 gap-y-1 items-baseline justify-between mb-3">
            <p className="text-lg font-semibold text-neutral-900" data-testid="catering-price-total">
              {perPersonKnown ? (
                <>
                  {money(total)}{' '}
                  <span className="text-sm font-normal text-neutral-500" data-testid="catering-price-per-person">
                    · {money(perPerson)}/person
                  </span>
                </>
              ) : (
                // The custom mix is not a legal order yet, so there is no price
                // to state. Printing the package's own price here is exactly how
                // the customer came to approve a number the server never charged.
                <span className="text-sm font-normal text-neutral-500">
                  Balance the categories to see your price
                </span>
              )}
            </p>
            <p className="text-[11px] text-neutral-400">Final price confirmed at checkout</p>
          </div>
          {step === 1 && (!step1Valid || addonMinUnmet) && <p className="text-xs text-neutral-600 mb-2" role="status" data-testid="catering-next-help">
            {!personsValid ? `Enter at least ${minPersons} guests.` : !eventDate || !eventTime ? 'Choose your event date and time to continue.' : datetimeIssue || (!picksValid ? 'Complete the required dish selections above to continue.' : `Complete the required add-ons in ${addonMinUnmet?.group.label || addonMinUnmet?.group.key}.`)}
          </p>}
          <div className="flex gap-3">
            {step === 1 ? (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 rounded-full border-2 border-neutral-200 py-3 text-xs tracking-[0.2em] uppercase font-medium text-neutral-600 hover:border-neutral-400 transition-colors"
                  data-testid="catering-cancel"
                >
                  Cancel
                </button>
                {/* Add-ons live on step 1 now, so a REQUIRED group must be
                    satisfied before moving on (the final CTA re-checks). */}
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  disabled={!step1Valid || !!addonMinUnmet}
                  className="flex-1 rounded-full py-3 text-xs tracking-[0.2em] uppercase font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: brandPrimary }}
                  data-testid="catering-next"
                >
                  Next
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 rounded-full border-2 border-neutral-200 py-3 text-xs tracking-[0.2em] uppercase font-medium text-neutral-600 hover:border-neutral-400 transition-colors"
                  data-testid="catering-back"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleAddToCart}
                  disabled={!!addonMinUnmet}
                  className="flex-1 rounded-full py-3 text-xs tracking-[0.2em] uppercase font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: brandPrimary }}
                  data-testid="catering-add-to-cart"
                >
                  Add to cart
                </button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CateringOrderModal;
